import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    PieChart, Pie, Cell, Tooltip as RTooltip, Legend, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_BLOCK_TYPES } from '../../hooks/useTimeblock';
import api from '../../services/api';
import ConfirmModal from '../common/ConfirmModal';
import {
    format,
    startOfDay, endOfDay,
    startOfWeek, endOfWeek,
    startOfMonth, endOfMonth,
    startOfYear, endOfYear,
    eachDayOfInterval, eachMonthOfInterval,
    addWeeks, subWeeks,
    addMonths, subMonths,
    addYears, subYears
} from 'date-fns';

// ---- Helpers ----

function formatDuration(ms) {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function formatPct(ratio) {
    return `${(ratio * 100).toFixed(1)}%`;
}

function getRange(date, mode) {
    if (mode === 'week')  return { start: startOfWeek(date), end: endOfWeek(date) };
    if (mode === 'month') return { start: startOfMonth(date), end: endOfMonth(date) };
    if (mode === 'year')  return { start: startOfYear(date), end: endOfYear(date) };
    return { start: date, end: date };
}

function getPeriodLabel(date, mode) {
    if (mode === 'week') {
        const { start, end } = getRange(date, 'week');
        return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
    }
    if (mode === 'month') return format(date, 'MMMM yyyy');
    if (mode === 'year')  return format(date, 'yyyy');
    return format(date, 'MMM d, yyyy');
}

function calcDayInterruptions(sessions) {
    const sorted = [...sessions].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    if (sorted.length <= 1) return 0;
    const span = new Date(sorted[sorted.length - 1].endTime) - new Date(sorted[0].startTime);
    const active = sorted.reduce((acc, s) => acc + (new Date(s.endTime) - new Date(s.startTime)), 0);
    return Math.max(0, span - active);
}

function buildBarEntry(label, records, blockTypes, getBlockType) {
    const entry = { label };
    blockTypes.forEach(bt => { entry[bt.label] = 0; });
    entry.Interruption = 0;
    records.forEach(record => {
        (record.sessions || []).forEach(s => {
            const bt = getBlockType(s.blockTypeId || s.blockId);
            const dur = new Date(s.endTime) - new Date(s.startTime);
            entry[bt.label] = (entry[bt.label] || 0) + dur;
        });
        entry.Interruption += calcDayInterruptions(record.sessions || []);
    });
    return entry;
}

const INTERRUPTION_COLOR = '#64748B';

function isoToHHmm(iso) {
    if (!iso) return '09:00';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function buildIso(dateStr, hhmm) {
    return new Date(`${dateStr}T${hhmm}:00`).toISOString();
}

const timeInputClass = 'w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary-button)] [color-scheme:dark]';

// ---- Session Edit/Add Modal ----
function SessionEditModal({ open, mode, initial, defaultDate, showDateField, blockTypes, onSave, onClose }) {
    const isEdit = mode === 'edit';
    // For edits, always anchor to the owning day record unless date picker is shown.
    const dateStr = showDateField ? (initial?.startTime ? initial.startTime.slice(0, 10) : defaultDate) : defaultDate;
    const [blockTypeId, setBlockTypeId] = useState(initial?.blockTypeId || blockTypes[0]?.id || '');
    const [startTime, setStartTime] = useState(initial ? isoToHHmm(initial.startTime) : '09:00');
    const [endTime, setEndTime] = useState(initial ? isoToHHmm(initial.endTime) : '10:00');
    const [date, setDate] = useState(dateStr);

    if (!open) return null;

    const useDate = showDateField ? date : dateStr;
    const startIso = buildIso(useDate, startTime);
    const endIso = buildIso(useDate, endTime);
    const validRange = new Date(endIso) > new Date(startIso);

    const handleSave = () => {
        if (!validRange) return;
        onSave({ blockTypeId, startTime: startIso, endTime: endIso });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[9500] flex items-center justify-center p-4">
            <div className="bg-[var(--surface-bg)] rounded-lg shadow-xl w-full max-w-sm p-6 space-y-4">
                <h3 className="text-base font-semibold text-[var(--text-primary)]">
                    {isEdit ? 'Edit Session' : 'Add Session'}
                </h3>
                <div className="space-y-3">
                    {showDateField && (
                        <div>
                            <label className="block text-xs text-[var(--text-muted)] mb-1">Date</label>
                            <input
                                type="date"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                                className={timeInputClass}
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-xs text-[var(--text-muted)] mb-1">Block Type</label>
                        <select
                            value={blockTypeId}
                            onChange={e => setBlockTypeId(e.target.value)}
                            className={timeInputClass}
                        >
                            {blockTypes.map(bt => (
                                <option key={bt.id} value={bt.id}>{bt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="block text-xs text-[var(--text-muted)] mb-1">Start Time</label>
                            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className={timeInputClass} />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs text-[var(--text-muted)] mb-1">End Time</label>
                            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className={timeInputClass} />
                        </div>
                    </div>
                </div>
                <div className="flex justify-center gap-3 pt-1">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--button-hover-bg)] rounded transition-colors border border-[var(--input-border)]">Cancel</button>
                    <button onClick={handleSave} disabled={!validRange} className="px-4 py-2 bg-[var(--primary-button)] text-white text-sm rounded hover:bg-[var(--primary-button-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Save</button>
                </div>
            </div>
        </div>
    );
}
const DATE_PICKER_SX = {
    '& .MuiInputBase-root': { backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '0.875rem' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--input-border)' },
    '& .MuiSvgIcon-root': { color: 'var(--text-muted)' }
};

// ---- Timeline Bar (day only) ----
function TimelineBar({ sessions, blockTypes }) {
    const [tooltip, setTooltip] = useState(null);
    const getBlockType = (id) => blockTypes.find(bt => bt.id === id) || { label: id, color: '#888' };

    const sorted = [...sessions].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    useEffect(() => {
        setTooltip(null);
    }, [sessions]);
    if (sorted.length === 0) return null;

    const minMs = new Date(sorted[0].startTime).getTime();
    const maxMs = new Date(sorted[sorted.length - 1].endTime).getTime();
    const totalSpan = maxMs - minMs || 1;

    const segments = [];
    let cursor = minMs;
    sorted.forEach((s, i) => {
        const sStart = new Date(s.startTime).getTime();
        const sEnd = new Date(s.endTime).getTime();
        if (sEnd <= sStart) return;
        if (sStart > cursor) {
            segments.push({
                type: 'gap', id: `gap-${i}:${cursor}:${sStart}`,
                startMs: cursor, endMs: sStart,
                widthPctRaw: ((sStart - cursor) / totalSpan) * 100,
                startTime: new Date(cursor).toISOString(), endTime: s.startTime
            });
        }
        const bt = getBlockType(s.blockTypeId || s.blockId);
        segments.push({
            type: 'session', id: `${s.id}:${s.startTime}:${s.endTime}`,
            startMs: sStart, endMs: sEnd,
            widthPctRaw: ((sEnd - sStart) / totalSpan) * 100,
            color: bt.color, label: bt.label,
            startTime: s.startTime, endTime: s.endTime, durationMs: sEnd - sStart
        });
        cursor = sEnd;
    });
    // Keep dynamic time scaling, but normalize widths so tiny segments stay visible
    // without pushing later segments out of the container.
    const MIN_SEGMENT_PCT = 0.8;
    const weighted = segments.map(seg => ({ ...seg, widthPct: Math.max(seg.widthPctRaw, MIN_SEGMENT_PCT) }));
    const totalWeightedPct = weighted.reduce((sum, seg) => sum + seg.widthPct, 0) || 1;
    const normalizedSegments = weighted.map(seg => ({
        ...seg,
        widthPct: (seg.widthPct / totalWeightedPct) * 100
    }));

    return (
        <div className="space-y-2">
            <div className="flex justify-between text-xs text-[var(--text-muted)]">
                <span>{format(new Date(minMs), 'h:mm a')}</span>
                <span>{format(new Date(maxMs), 'h:mm a')}</span>
            </div>
            <div className="relative flex h-10 rounded-lg overflow-hidden">
                {normalizedSegments.map(seg => (
                    <div
                        key={seg.id}
                        style={{
                            width: `${seg.widthPct}%`,
                            backgroundColor: seg.type === 'gap' ? 'var(--surface-bg-darker)' : seg.color,
                            flexShrink: 0
                        }}
                        onMouseEnter={e => setTooltip({ seg, x: e.clientX, y: e.clientY })}
                        onMouseMove={e => setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
                        onMouseLeave={() => setTooltip(null)}
                        className="cursor-default"
                    />
                ))}
            </div>
            {tooltip && (
                <div className="fixed z-50 pointer-events-none px-3 py-2 rounded-lg shadow-lg text-xs space-y-0.5"
                    style={{ left: tooltip.x + 12, top: tooltip.y - 60, backgroundColor: 'var(--surface-bg)', border: '1px solid var(--surface-border)', color: 'var(--text-primary)' }}>
                    {tooltip.seg.type === 'gap' ? (
                        <>
                            <div className="font-semibold text-[var(--text-muted)]">Interruption</div>
                            <div>{format(new Date(tooltip.seg.startTime), 'h:mm a')} – {format(new Date(tooltip.seg.endTime), 'h:mm a')}</div>
                            <div>{formatDuration(tooltip.seg.endMs - tooltip.seg.startMs)}</div>
                        </>
                    ) : (
                        <>
                            <div className="font-semibold" style={{ color: tooltip.seg.color }}>{tooltip.seg.label}</div>
                            <div>{format(new Date(tooltip.seg.startTime), 'h:mm a')} – {format(new Date(tooltip.seg.endTime), 'h:mm a')}</div>
                            <div>{formatDuration(tooltip.seg.durationMs)}</div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ---- Pie tooltip ----
function PieTooltipContent({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    return (
        <div className="px-3 py-2 rounded-lg shadow-lg text-xs space-y-0.5"
            style={{ backgroundColor: 'var(--surface-bg)', border: '1px solid var(--surface-border)', color: 'var(--text-primary)' }}>
            <div className="font-semibold" style={{ color: d.payload.color }}>{d.name}</div>
            <div>{formatDuration(d.payload.durationMs)}</div>
            <div>{formatPct(d.payload.ratio)}</div>
        </div>
    );
}

// ---- Bar chart tooltip ----
function BarTooltipContent({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const nonZero = payload.filter(p => p.value > 0);
    if (!nonZero.length) return null;
    return (
        <div className="px-3 py-2 rounded-lg shadow-lg text-xs space-y-1"
            style={{ backgroundColor: 'var(--surface-bg)', border: '1px solid var(--surface-border)', color: 'var(--text-primary)' }}>
            <div className="font-semibold mb-1">{label}</div>
            {nonZero.map(p => (
                <div key={p.dataKey} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.fill }} />
                    <span>{p.name}: {formatDuration(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

// ---- Main Analytics component ----
function Analytics({ hook }) {
    const { selectedDate, setSelectedDate, dayData, loading, deleteSession: hookDeleteSession, updateSession: hookUpdateSession, addSession: hookAddSession, clearSessions: hookClearSessions } = hook;
    const settings = useSelector(state => state.settings.settings);
    const blockTypes = settings?.timeblockTypes || DEFAULT_BLOCK_TYPES;
    const getBlockType = useCallback(
        (id) => blockTypes.find(bt => bt.id === id) || { label: id, color: '#888' },
        [blockTypes]
    );

    const [viewMode, setViewMode] = useState('day');
    const [rangeDate, setRangeDate] = useState(new Date());
    const [rangeData, setRangeData] = useState([]);
    const [rangeLoading, setRangeLoading] = useState(false);
    const [sortCol, setSortCol] = useState('start');
    const [sortDir, setSortDir] = useState('asc');
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [sessionEditRow, setSessionEditRow] = useState(null); // row for edit, or null
    const [sessionAddOpen, setSessionAddOpen] = useState(false);

    const loadRange = useCallback(async (start, end) => {
        setRangeLoading(true);
        try {
            const res = await api.get(`/timeblocks?start=${format(start, 'yyyy-MM-dd')}&end=${format(end, 'yyyy-MM-dd')}`);
            setRangeData(res.data);
        } catch {
            setRangeData([]);
        } finally {
            setRangeLoading(false);
        }
    }, []);

    useEffect(() => {
        if (viewMode !== 'day') {
            const { start, end } = getRange(rangeDate, viewMode);
            loadRange(start, end);
        }
    }, [viewMode, rangeDate, loadRange]);

    const handleViewModeChange = (mode) => {
        if (mode !== 'day') setRangeDate(selectedDate);
        setViewMode(mode);
        setSortCol('start');
        setSortDir('asc');
    };

    const navigatePeriod = (dir) => {
        setRangeDate(prev => {
            if (viewMode === 'week')  return dir === 'next' ? addWeeks(prev, 1)  : subWeeks(prev, 1);
            if (viewMode === 'month') return dir === 'next' ? addMonths(prev, 1) : subMonths(prev, 1);
            if (viewMode === 'year')  return dir === 'next' ? addYears(prev, 1)  : subYears(prev, 1);
            return prev;
        });
    };

    const handleDeleteSession = useCallback(async (row) => {
        setPendingDeleteId(null);
        if (viewMode === 'day') {
            hookDeleteSession(row.id);
        } else {
            const date = row.date;
            const record = rangeData.find(r => r.date === date);
            if (!record) return;
            const updatedSessions = record.sessions.filter(s => s.id !== row.id);
            await api.put(`/timeblocks/${date}`, { ...record, sessions: updatedSessions });
            setRangeData(prev => prev.map(r => r.date === date ? { ...r, sessions: updatedSessions } : r));
        }
    }, [viewMode, hookDeleteSession, rangeData]);

    const handleClearAll = useCallback(async () => {
        setPendingDeleteId(null);
        if (viewMode === 'day') {
            hookClearSessions();
        } else {
            const daysWithSessions = rangeData.filter(r => r.sessions?.length > 0);
            await Promise.all(daysWithSessions.map(r =>
                api.put(`/timeblocks/${r.date}`, { ...r, sessions: [] })
            ));
            setRangeData(prev => prev.map(r => ({ ...r, sessions: [] })));
        }
    }, [viewMode, hookClearSessions, rangeData]);

    const handleEditSession = useCallback(async (payload) => {
        const row = sessionEditRow;
        if (!row) return;
        setSessionEditRow(null);
        const targetDate = viewMode === 'day' ? format(selectedDate, 'yyyy-MM-dd') : row.date;
        const payloadStartDate = format(new Date(payload.startTime), 'yyyy-MM-dd');
        const payloadEndDate = format(new Date(payload.endTime), 'yyyy-MM-dd');
        if (payloadStartDate !== targetDate || payloadEndDate !== targetDate) return;
        if (viewMode === 'day') {
            hookUpdateSession(row.id, payload);
        } else {
            const date = row.date;
            const record = rangeData.find(r => r.date === date);
            if (!record) return;
            const updatedSessions = record.sessions.map(s =>
                s.id === row.id ? { ...s, ...payload } : s
            );
            await api.put(`/timeblocks/${date}`, { ...record, sessions: updatedSessions });
            setRangeData(prev => prev.map(r => r.date === date ? { ...r, sessions: updatedSessions } : r));
        }
    }, [viewMode, sessionEditRow, hookUpdateSession, rangeData, selectedDate]);

    const handleAddSession = useCallback(async (payload) => {
        setSessionAddOpen(false);
        const dateStr = format(new Date(payload.startTime), 'yyyy-MM-dd');
        const endDateStr = format(new Date(payload.endTime), 'yyyy-MM-dd');
        if (endDateStr !== dateStr) return;
        if (viewMode === 'day') {
            const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
            if (dateStr !== selectedDateStr) return;
            hookAddSession(payload);
        } else {
            let record = rangeData.find(r => r.date === dateStr);
            if (!record) {
                record = { date: dateStr, scheduledBlocks: [], sessions: [] };
                setRangeData(prev => [...prev, record].sort((a, b) => a.date.localeCompare(b.date)));
            }
            const session = { id: uuidv4(), blockId: null, blockTypeId: payload.blockTypeId, startTime: payload.startTime, endTime: payload.endTime };
            const updatedSessions = [...(record.sessions || []), session].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
            await api.put(`/timeblocks/${dateStr}`, { ...record, sessions: updatedSessions });
            setRangeData(prev => prev.map(r => r.date === dateStr ? { ...r, sessions: updatedSessions } : r));
        }
    }, [viewMode, hookAddSession, rangeData, selectedDate]);

    // Clamp day sessions to selected-day bounds to avoid cross-day spans skewing day analytics.
    const daySessions = useMemo(() => {
        const dayStart = startOfDay(selectedDate);
        const dayEnd = endOfDay(selectedDate);
        return (dayData.sessions || [])
            .map((s) => {
                const start = new Date(s.startTime);
                const end = new Date(s.endTime);
                const clampedStart = start < dayStart ? dayStart : start;
                const clampedEnd = end > dayEnd ? dayEnd : end;
                if (clampedEnd <= clampedStart) return null;
                return {
                    ...s,
                    startTime: clampedStart.toISOString(),
                    endTime: clampedEnd.toISOString()
                };
            })
            .filter(Boolean)
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    }, [dayData.sessions, selectedDate]);
    const rangeSessions = useMemo(
        () => rangeData.flatMap(r => (r.sessions || []).map(s => ({ ...s, date: r.date }))),
        [rangeData]
    );
    const activeSessions = viewMode === 'day' ? daySessions : rangeSessions;

    // Interruptions (sum across days for range views)
    const interruptionMs = useMemo(() => {
        if (viewMode === 'day') return calcDayInterruptions(daySessions);
        return rangeData.reduce((acc, r) => acc + calcDayInterruptions(r.sessions || []), 0);
    }, [viewMode, daySessions, rangeData]);

    const activeMs = useMemo(
        () => activeSessions.reduce((acc, s) => acc + (new Date(s.endTime) - new Date(s.startTime)), 0) || 1,
        [activeSessions]
    );
    const grandTotalMs = activeMs + interruptionMs;

    // Pie data — aggregated by block type + interruption slice
    const pieData = useMemo(() => {
        const map = {};
        activeSessions.forEach(s => {
            const btId = s.blockTypeId || s.blockId;
            const bt = getBlockType(btId);
            const dur = new Date(s.endTime) - new Date(s.startTime);
            if (!map[btId]) map[btId] = { name: bt.label, durationMs: 0, color: bt.color };
            map[btId].durationMs += dur;
        });
        const result = Object.values(map).map(r => ({
            ...r, value: r.durationMs, ratio: r.durationMs / grandTotalMs
        }));
        if (interruptionMs > 0) {
            result.push({
                name: 'Interruption', value: interruptionMs, durationMs: interruptionMs,
                ratio: interruptionMs / grandTotalMs, color: INTERRUPTION_COLOR
            });
        }
        return result;
    }, [activeSessions, interruptionMs, grandTotalMs, getBlockType]);

    // Bar chart data for range views
    const barData = useMemo(() => {
        if (viewMode === 'day') return null;
        if (viewMode === 'year') {
            return eachMonthOfInterval({ start: startOfYear(rangeDate), end: endOfYear(rangeDate) })
                .map(month => {
                    const monthStr = format(month, 'yyyy-MM');
                    const records = rangeData.filter(r => r.date.startsWith(monthStr));
                    return buildBarEntry(format(month, 'MMM'), records, blockTypes, getBlockType);
                });
        }
        const { start, end } = getRange(rangeDate, viewMode);
        return eachDayOfInterval({ start, end }).map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const record = rangeData.find(r => r.date === dateStr);
            const label = viewMode === 'week' ? format(day, 'EEE d') : format(day, 'd');
            return buildBarEntry(label, record ? [record] : [], blockTypes, getBlockType);
        });
    }, [viewMode, rangeDate, rangeData, blockTypes, getBlockType]);

    // Block types present in current data (for bar chart bars)
    const presentBlockTypes = useMemo(() => {
        const ids = new Set(activeSessions.map(s => s.blockTypeId || s.blockId));
        return blockTypes.filter(bt => ids.has(bt.id));
    }, [activeSessions, blockTypes]);

    // Session rows for breakdown table
    const sessionRows = useMemo(() => {
        return activeSessions.map(s => {
            const bt = getBlockType(s.blockTypeId || s.blockId);
            const durationMs = new Date(s.endTime) - new Date(s.startTime);
            return { ...s, bt, durationMs, ratio: durationMs / activeMs };
        });
    }, [activeSessions, activeMs, getBlockType]);

    const sorted = useMemo(() => {
        return [...sessionRows].sort((a, b) => {
            let va, vb;
            if (sortCol === 'date')          { va = (a.date || '') + a.startTime; vb = (b.date || '') + b.startTime; }
            else if (sortCol === 'start')    { va = a.startTime;   vb = b.startTime; }
            else if (sortCol === 'end')      { va = a.endTime;     vb = b.endTime; }
            else if (sortCol === 'duration') { va = a.durationMs;  vb = b.durationMs; }
            else if (sortCol === 'pct')      { va = a.ratio;       vb = b.ratio; }
            else                             { va = a.bt.label;    vb = b.bt.label; }
            if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
            return sortDir === 'asc' ? va - vb : vb - va;
        });
    }, [sessionRows, sortCol, sortDir]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir(['start', 'end', 'date'].includes(col) ? 'asc' : 'desc'); }
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span className="material-symbols-outlined text-xs opacity-30">unfold_more</span>;
        return <span className="material-symbols-outlined text-xs">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>;
    };

    const isLoading = viewMode === 'day' ? loading : rangeLoading;
    const hasData = viewMode === 'day' ? daySessions.length > 0 : rangeSessions.length > 0;
    const isRangeView = viewMode !== 'day';
    const chartDataKey = useMemo(() => {
        const sessionKey = activeSessions
            .map(s => `${s.id}:${s.blockTypeId || s.blockId}:${s.startTime}:${s.endTime}`)
            .join('|');
        return `${viewMode}:${format(rangeDate, 'yyyy-MM-dd')}:${interruptionMs}:${sessionKey}`;
    }, [activeSessions, viewMode, rangeDate, interruptionMs]);

    return (
        <div className="space-y-6">
            {/* Controls row */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                {/* View mode pills */}
                <div className="flex gap-1 bg-[var(--surface-bg-lighter)] p-1 rounded-lg">
                    {['day', 'week', 'month', 'year'].map(mode => (
                        <button
                            key={mode}
                            onClick={() => handleViewModeChange(mode)}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-all capitalize ${
                                viewMode === mode
                                    ? 'bg-[var(--surface-bg)] text-[var(--text-primary)] shadow-sm'
                                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                            }`}
                        >
                            {mode}
                        </button>
                    ))}
                </div>

                {/* Date / period navigator */}
                {viewMode === 'day' ? (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            value={selectedDate}
                            onChange={(d) => d && setSelectedDate(d)}
                            slotProps={{ textField: { size: 'small', sx: DATE_PICKER_SX } }}
                        />
                    </LocalizationProvider>
                ) : (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => navigatePeriod('prev')}
                            className="p-1.5 rounded-md hover:bg-[var(--button-hover-bg)] text-[var(--text-muted)] transition-colors"
                        >
                            <span className="material-symbols-outlined text-base">chevron_left</span>
                        </button>
                        <span className="text-sm font-medium text-[var(--text-primary)] min-w-[180px] text-center">
                            {getPeriodLabel(rangeDate, viewMode)}
                        </span>
                        <button
                            onClick={() => navigatePeriod('next')}
                            className="p-1.5 rounded-md hover:bg-[var(--button-hover-bg)] text-[var(--text-muted)] transition-colors"
                        >
                            <span className="material-symbols-outlined text-base">chevron_right</span>
                        </button>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="text-center py-12 text-[var(--text-muted)] text-sm">Loading…</div>
            ) : !hasData ? (
                <div className="text-center py-12 text-[var(--text-muted)] text-sm">
                    No recorded sessions for this {viewMode}.
                    <br />
                    <span className="text-xs">Start a timer on the Schedule tab to record sessions.</span>
                </div>
            ) : (
                <>
                    {/* Day: timeline bar */}
                    {!isRangeView && (
                        <div className="space-y-2">
                            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Timeline</h3>
                            <TimelineBar key={`timeline-${chartDataKey}`} sessions={daySessions} blockTypes={blockTypes} />
                        </div>
                    )}

                    {/* Range views: stacked bar chart */}
                    {isRangeView && barData && (
                        <div className="space-y-2">
                            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Activity</h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart key={`activity-${chartDataKey}`} data={barData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" vertical={false} />
                                        <XAxis
                                            dataKey="label"
                                            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                            axisLine={false} tickLine={false}
                                        />
                                        <YAxis
                                            tickFormatter={ms => ms === 0 ? '0' : ms >= 3600000 ? `${(ms / 3600000).toFixed(1)}h` : `${Math.round(ms / 60000)}m`}
                                            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                                            axisLine={false} tickLine={false} width={36}
                                        />
                                        <RTooltip content={<BarTooltipContent />} cursor={{ fill: 'var(--button-hover-bg)' }} />
                                        <Legend formatter={v => <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{v}</span>} />
                                        {presentBlockTypes.map(bt => (
                                            <Bar key={bt.id} dataKey={bt.label} stackId="a" fill={bt.color} radius={0} />
                                        ))}
                                        {interruptionMs > 0 && (
                                            <Bar dataKey="Interruption" stackId="a" fill={INTERRUPTION_COLOR} radius={0} />
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* Pie chart */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Time Distribution</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart key={`distribution-${chartDataKey}`}>
                                    <Pie
                                        data={pieData}
                                        cx="50%" cy="50%"
                                        innerRadius={50} outerRadius={90}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <RTooltip content={<PieTooltipContent />} />
                                    <Legend formatter={v => <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{v}</span>} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Breakdown table */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                                Breakdown{' '}
                                <span className="text-[var(--text-muted)] font-normal">({sessionRows.length} runs)</span>
                            </h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => { setSessionAddOpen(true); setSessionEditRow(null); setPendingDeleteId(null); }}
                                    className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--primary-button)] transition-colors"
                                >
                                    <span className="material-symbols-outlined text-sm">add</span>
                                    Add Session
                                </button>
                                {sessionRows.length > 0 && (
                                    <button
                                        onClick={() => { setShowClearConfirm(true); setPendingDeleteId(null); }}
                                        className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-sm">delete_sweep</span>
                                        Clear All
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="overflow-x-auto rounded-lg border border-[var(--surface-border)]">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-[var(--surface-border)] bg-[var(--surface-bg-lighter)]">
                                        {isRangeView && (
                                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                                                onClick={() => handleSort('date')}>
                                                <div className="flex items-center gap-1">Date <SortIcon col="date" /></div>
                                            </th>
                                        )}
                                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                                            onClick={() => handleSort('label')}>
                                            <div className="flex items-center gap-1">Block Type <SortIcon col="label" /></div>
                                        </th>
                                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                                            onClick={() => handleSort('start')}>
                                            <div className="flex items-center justify-end gap-1">Start <SortIcon col="start" /></div>
                                        </th>
                                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                                            onClick={() => handleSort('end')}>
                                            <div className="flex items-center justify-end gap-1">End <SortIcon col="end" /></div>
                                        </th>
                                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                                            onClick={() => handleSort('duration')}>
                                            <div className="flex items-center justify-end gap-1">Duration <SortIcon col="duration" /></div>
                                        </th>
                                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                                            onClick={() => handleSort('pct')}>
                                            <div className="flex items-center justify-end gap-1">% of Total <SortIcon col="pct" /></div>
                                        </th>
                                        <th className="w-px px-2 py-2.5" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map((row, i) => (
                                        <tr key={row.id}
                                            className={`border-b border-[var(--surface-border)] last:border-0 ${
                                                i % 2 === 0 ? 'bg-[var(--surface-bg)]' : 'bg-[var(--surface-bg-lighter)]'
                                            }`}>
                                            {isRangeView && (
                                                <td className="px-4 py-3 text-[var(--text-muted)] text-xs tabular-nums">
                                                    {row.date ? format(new Date(row.date + 'T00:00:00'), 'MMM d') : '—'}
                                                </td>
                                            )}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: row.bt.color }} />
                                                    <span className="text-[var(--text-primary)]">{row.bt.label}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right text-[var(--text-secondary)] tabular-nums">
                                                {format(new Date(row.startTime), 'h:mm a')}
                                            </td>
                                            <td className="px-4 py-3 text-right text-[var(--text-secondary)] tabular-nums">
                                                {format(new Date(row.endTime), 'h:mm a')}
                                            </td>
                                            <td className="px-4 py-3 text-right text-[var(--text-secondary)] tabular-nums">
                                                {formatDuration(row.durationMs)}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <div className="w-12 h-1.5 bg-[var(--surface-bg-darker)] rounded-full overflow-hidden">
                                                        <div className="h-full rounded-full"
                                                            style={{ width: `${row.ratio * 100}%`, backgroundColor: row.bt.color }} />
                                                    </div>
                                                    <span className="text-[var(--text-secondary)] w-10 text-right tabular-nums">
                                                        {formatPct(row.ratio)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-2 py-3 text-right">
                                                <div className="flex items-center justify-end gap-0 -space-x-2">
                                                    <button
                                                        onClick={() => { setSessionEditRow(row); setSessionAddOpen(false); setPendingDeleteId(null); }}
                                                        className="px-2 py-2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                                                        title="Edit session"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">edit</span>
                                                    </button>
                                                    {pendingDeleteId === row.id ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleDeleteSession(row)}
                                                                className="px-2 py-2 text-[var(--error)] hover:text-[var(--error-hover)]"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px]">check</span>
                                                            </button>
                                                            <button
                                                                onClick={() => setPendingDeleteId(null)}
                                                                className="px-2 py-2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                                                            >
                                                                <span className="material-symbols-outlined text-[20px]">close</span>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={() => setPendingDeleteId(row.id)}
                                                            className="px-2 py-2 text-[var(--error)] hover:text-[var(--error-hover)]"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">delete</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <ConfirmModal
                isOpen={showClearConfirm}
                onClose={() => setShowClearConfirm(false)}
                onConfirm={handleClearAll}
                title="Clear All Sessions"
                message="Are you sure you want to clear all recorded sessions? This cannot be undone."
                confirmText="Clear All"
                cancelText="Cancel"
                variant="danger"
            />

            {(sessionEditRow || sessionAddOpen) && (
                <SessionEditModal
                    key={sessionEditRow ? `edit-${sessionEditRow.id}` : 'add'}
                    open={!!sessionEditRow || sessionAddOpen}
                    mode={sessionEditRow ? 'edit' : 'add'}
                    initial={sessionEditRow ? { blockTypeId: sessionEditRow.blockTypeId || sessionEditRow.blockId, startTime: sessionEditRow.startTime, endTime: sessionEditRow.endTime } : undefined}
                    defaultDate={viewMode === 'day' ? format(selectedDate, 'yyyy-MM-dd') : format(getRange(rangeDate, viewMode).start, 'yyyy-MM-dd')}
                    showDateField={isRangeView && sessionAddOpen}
                    blockTypes={blockTypes}
                    onSave={(payload) => sessionEditRow ? handleEditSession(payload) : handleAddSession(payload)}
                    onClose={() => { setSessionEditRow(null); setSessionAddOpen(false); }}
                />
            )}
        </div>
    );
}

export default Analytics;
